"use client";

import * as stylex from "@stylexjs/stylex";
import { fieldStyles as styles } from "./editable-field.stylex";
import { useEffect, useLayoutEffect, useRef } from "react";

import type { EditorWarning } from "./editor-warning";
export type EditableFieldViewProps = Readonly<{
  value: string | null;
  liveValue: string;
  onChange(next: string): void;
  maxLength: number;
  ariaLabel: string;
  placeholder?: string;
  variant?: "body" | "beatTitle" | "linkLabel" | "cta";
  tag?: "p" | "span" | "div";
  fitPreview?: (text: string) => string | null;
  lintWarnings?: readonly EditorWarning[];
  autoFocus?: boolean;
  editing: boolean;
  loading: boolean;
}>;

export function EditableFieldView({
  value,
  liveValue,
  onChange,
  maxLength,
  ariaLabel,
  placeholder,
  variant = "body",
  tag: Tag = "p",
  fitPreview,
  lintWarnings = [],
  autoFocus = false,
  editing,
  loading,
}: EditableFieldViewProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const text = value ?? liveValue;
  useLayoutEffect(() => {
    const el = ref.current;

    if (!el || !editing) return;

    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  });
  useEffect(() => {
    if (autoFocus && editing && !loading) ref.current?.focus();
  }, [autoFocus, editing, loading]);

  if (!editing)
    return (
      <Tag {...stylex.props(styles.text, styles[variant])}>{liveValue}</Tag>
    );

  const fit = fitPreview?.(text) ?? null;
  return (
    <span {...stylex.props(styles.field)}>
      <textarea
        ref={ref}
        rows={1}
        value={text}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-label={ariaLabel}
        disabled={loading}
        onChange={(event) => onChange(event.target.value)}
        {...stylex.props(styles.text, styles[variant], styles.input)}
      />
      <span {...stylex.props(styles.meta)}>
        <span
          {...stylex.props(
            styles.caption,
            text.length >= maxLength && styles.warning,
          )}
        >
          {text.length}/{maxLength}
        </span>
        {fit ? (
          <span {...stylex.props(styles.caption)}>Shows as: “{fit}”</span>
        ) : null}
      </span>
      {lintWarnings.length > 0 ? (
        <span {...stylex.props(styles.warnings)}>
          {lintWarnings.map((warning) => (
            <span
              key={warning.rule}
              {...stylex.props(styles.caption, styles.warning)}
            >
              {warning.message}
            </span>
          ))}
        </span>
      ) : null}
    </span>
  );
}
